import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, gql } from 'apollo-angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Util from 'src/app/_helpers/util';
import { DataService } from 'src/app/_services/data';
import { CompanyService } from 'src/app/_services/company.services';


const getOpenBillingQuery = gql`
  query {
    getOpenBilling {
      proformaInvoiceId
      proformaInvoiceNo
      proformaInvoiceDate
      proformaInvoiceExpiredDate
      paymentMethod
      proformaInvoiceStatus
      openBillingPriceCurrency {
        price
        currency
      }
    }
  }
`;

const getBillingHistoryQuery = gql`
  query {
    getBillingHistory {
      proformaInvoiceId
      proformaInvoiceNo
      proformaInvoiceDate
      invoiceId
      invoiceDate
      invoiceNo
      proformaInvoiceStatus
      historyBillingPriceCurrency {
        price
        currency
      }
    }
  }
`;

@Component({
  selector: 'app-subscription-list',
  templateUrl: './subscription-list.component.html',
  styleUrls: ['./subscription-list.component.scss']
})
export class SubscriptionListComponent implements OnInit, OnDestroy {
  currentLang: string = 'id';
  isLoading: boolean = false;
  loadingError: any = null;

  // Data arrays
  currentSubscriptions: any[] = [];
  activeInvoices: any[] = [];
  purchaseHistory: any[] = [];
  
  // Grouped subscriptions
  groupedSubscriptions: { [key: string]: any[] } = {};
  subscriptionOrder = ['SUBSCRIPTION', 'VOUCHER', 'ADDONS'];

  private destroy$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    private apollo: Apollo,
    private dataService: DataService,
    private companyService: CompanyService
  ) { }

  ngOnInit(): void {
    this.currentLang = localStorage.getItem('language') || 'id';
    this.loadAllData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all subscription data
   */
  loadAllData(): void {
    this.isLoading = true;
    
    // Load current subscriptions
    this.loadCurrentSubscriptions();
    
    // Load active invoices
    this.loadActiveInvoices();
    
    // Load purchase history
    this.loadPurchaseHistory();
  }

  /**
   * Load current subscriptions from API
   */
  private loadCurrentSubscriptions(): void {
    this.companyService.getgetActiveFhProduct().pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          if (result.data && result.data.getActiveFhProduct) {
            this.currentSubscriptions = result.data.getActiveFhProduct;
            this.groupSubscriptionsByType();
          }
          console.log('Current subscriptions loaded:', this.currentSubscriptions);
        },
        (error: any) => {
          console.error('Error loading current subscriptions:', error);
          this.loadingError = error;
          this.currentSubscriptions = [];
        }
      );
  }

  /**
   * Load active invoices from API
   */
  private loadActiveInvoices(): void {
    this.apollo.query({
      query: getOpenBillingQuery,
      fetchPolicy: 'no-cache'
    }).pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          if (result.data && result.data.getOpenBilling) {
            this.activeInvoices = result.data.getOpenBilling;
          }
          console.log('Active invoices loaded:', this.activeInvoices);
        },
        (error: any) => {
          console.error('Error loading active invoices:', error);
          this.loadingError = error;
          this.activeInvoices = [];
        }
      );
  }

  /**
   * Load purchase history from API
   */
  private loadPurchaseHistory(): void {
    this.apollo.query({
      query: getBillingHistoryQuery,
      fetchPolicy: 'no-cache'
    }).pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          if (result.data && result.data.getBillingHistory) {
            this.purchaseHistory = result.data.getBillingHistory;
          }
          this.isLoading = false;
          console.log('Purchase history loaded:', this.purchaseHistory);
        },
        (error: any) => {
          console.error('Error loading purchase history:', error);
          this.loadingError = error;
          this.purchaseHistory = [];
          this.isLoading = false;
        }
      );
  }

  /**
   * Group subscriptions by product category in specific order
   */
  private groupSubscriptionsByType(): void {
    this.groupedSubscriptions = {};
    
    // Initialize groups
    this.subscriptionOrder.forEach(type => {
      this.groupedSubscriptions[type] = [];
    });
    
    // Organize subscriptions into groups
    this.currentSubscriptions.forEach(subscription => {
      const category = subscription.productCategory || 'SUBSCRIPTION';
      if (this.groupedSubscriptions[category]) {
        this.groupedSubscriptions[category].push(subscription);
      }
    });
    
    console.log('Grouped subscriptions:', this.groupedSubscriptions);
  }

  /**
   * Switch language
   */
  switchLang(lang: string): void {
    this.translate.use(lang);
    localStorage.setItem('language', lang);
    this.currentLang = lang;
  }

  formatDisplayId(val:any): any {
    return Util.formatDisplayId(val)
  }

}
